import { create } from "zustand";

export const TOUR_TYPES = {
  DEALS_TABLE: "dealsTable",
  DEAL_DETAIL: "dealDetail",
  MOBILE_DEALS_TABLE: "mobileDealsTable",
  MOBILE_DEAL_DETAIL: "mobileDealDetail",
  SIDEBAR: "sidebar",
} as const;

export type TourType = (typeof TOUR_TYPES)[keyof typeof TOUR_TYPES];

const TOUR_STORAGE_KEYS: Record<TourType, string> = {
  [TOUR_TYPES.DEALS_TABLE]: "hasSeenDealsTour",
  [TOUR_TYPES.DEAL_DETAIL]: "hasSeenDealDetailTour",
  [TOUR_TYPES.MOBILE_DEALS_TABLE]: "hasSeenMobileTour",
  [TOUR_TYPES.MOBILE_DEAL_DETAIL]: "hasSeenMobileDealTour",
  [TOUR_TYPES.SIDEBAR]: "hasSeenSidebarTour",
};

interface TourState {
  // Tour states - tracks which tours are active
  activeTours: Record<string, boolean>;

  // Tour completion states - tracks which tours have been completed
  completedTours: Record<string, boolean>;

  // Actions
  initializeTours: () => void;
  startTour: (tourType: TourType) => boolean;
  endTour: (tourType: TourType) => void;
  skipTour: (tourType: TourType) => void;
  isTourActive: (tourType: TourType) => boolean;
  isTourCompleted: (tourType: TourType) => boolean;
  resetTour: (tourType: TourType) => void;
  resetAllTours: () => void;
}

export const useTourStore = create<TourState>((set, get) => ({
  // Tour states - tracks which tours are active
  activeTours: {},

  // Tour completion states - tracks which tours have been completed
  completedTours: {},

  // Initialize tour states from localStorage
  initializeTours: () => {
    const completedTours: Record<string, boolean> = {};
    Object.entries(TOUR_STORAGE_KEYS).forEach(([tourType, storageKey]) => {
      completedTours[tourType] = !!localStorage.getItem(storageKey);
    });
    set({ completedTours });
  },

  // Start a specific tour
  startTour: (tourType: TourType) => {
    const { completedTours } = get();

    // Don't start if already completed
    if (completedTours[tourType]) {
      return false;
    }

    set((state) => ({
      activeTours: { ...state.activeTours, [tourType]: true },
    }));

    return true;
  },

  // End a specific tour (mark as completed)
  endTour: (tourType: TourType) => {
    const storageKey = TOUR_STORAGE_KEYS[tourType];

    if (storageKey) {
      localStorage.setItem(storageKey, "true");
    }

    set((state) => ({
      activeTours: { ...state.activeTours, [tourType]: false },
      completedTours: { ...state.completedTours, [tourType]: true },
    }));
  },

  // Skip a tour (same as end)
  skipTour: (tourType: TourType) => {
    get().endTour(tourType);
  },

  // Check if a tour is active
  isTourActive: (tourType: TourType) => {
    const { activeTours } = get();
    return !!activeTours[tourType];
  },

  // Check if a tour has been completed
  isTourCompleted: (tourType: TourType) => {
    const { completedTours } = get();
    return !!completedTours[tourType];
  },

  // Reset a tour (allow it to be shown again)
  resetTour: (tourType: TourType) => {
    const storageKey = TOUR_STORAGE_KEYS[tourType];
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }

    set((state) => ({
      completedTours: { ...state.completedTours, [tourType]: false },
      activeTours: { ...state.activeTours, [tourType]: false },
    }));
  },

  // Reset all tours
  resetAllTours: () => {
    Object.values(TOUR_STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });

    set({
      activeTours: {},
      completedTours: {},
    });
  },
}));
